import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  AlertTriangle, Users, Activity, Shield, Zap, Brain, Clock,
  ChevronRight, Loader2, Heart, TrendingUp, FileText, Bell,
  Droplets, MessageCircle, Eye, StickyNote, Play, CheckCircle
} from 'lucide-react';

const SIGNAL_META = {
  predictive: { label: 'Predictive', color: 'text-rose-400', bg: 'bg-rose-500/10' },
  support: { label: 'Support', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  engagement: { label: 'Engagement', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  mindflo: { label: 'MindFlo', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  labs: { label: 'Labs', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
};

const TABS = [
  { key: 'dashboard', label: 'Dashboard', path: '/centre/dashboard' },
  { key: 'subscribers', label: 'Subscribers', path: '/centre/subscribers' },
  { key: 'actions', label: 'Actions', path: '/centre/actions' },
];

function PriorityBar({ score }) {
  const color = score >= 50 ? 'bg-rose-500' : score >= 25 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${Math.min(score, 100)}%` }} />
    </div>
  );
}

function SignalDots({ signals }) {
  return (
    <div className="flex gap-1">
      {Object.entries(signals || {}).filter(([, v]) => v > 0).map(([k, v]) => {
        const m = SIGNAL_META[k];
        return m ? (
          <div key={k} className={`w-5 h-5 rounded-full ${m.bg} flex items-center justify-center`} title={`${m.label}: ${v}`}>
            <span className={`text-[8px] font-bold ${m.color}`}>{Math.round(v / 10)}</span>
          </div>
        ) : null;
      })}
    </div>
  );
}

export default function OpsCockpit() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = TABS.find(t => location.pathname === t.path)?.key || 'dashboard';

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-5" data-testid="ops-cockpit">
        <div>
          <h1 className="text-xl font-['Outfit'] font-semibold text-adm-primary">Operations Cockpit</h1>
          <p className="text-xs text-adm-tertiary mt-1">Prioritized operational view for your scope</p>
        </div>

        <div className="flex gap-1 border-b border-white/[0.06]" data-testid="ops-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => navigate(t.path)}
              className={`px-4 py-2.5 text-xs font-['Work_Sans'] border-b-2 transition-colors ${currentTab === t.key ? 'border-adm-accent text-adm-accent' : 'border-transparent text-adm-tertiary hover:text-adm-secondary'}`}
              data-testid={`ops-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {currentTab === 'dashboard' && <DashboardView navigate={navigate} />}
        {currentTab === 'subscribers' && <SubscribersView navigate={navigate} />}
        {currentTab === 'actions' && <ActionsView />}
      </div>
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════
// DASHBOARD VIEW
// ═══════════════════════════════════════════════════════════

function DashboardView({ navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/ops/dashboard`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 text-adm-accent animate-spin" /></div>;
  if (!data) return <div className="text-center py-16 text-adm-tertiary text-xs">Unable to load dashboard</div>;

  const { total_subscribers, top_priority, risk_distribution, support, mindflo, execution_gaps, scope } = data;

  return (
    <div className="space-y-5">
      {/* Scope indicator */}
      <div className="flex items-center gap-2 text-[10px] text-adm-tertiary">
        <Shield className="w-3 h-3" />
        <span>Scope: <strong className="text-adm-secondary capitalize">{scope?.type || 'global'}</strong></span>
        <span>{total_subscribers} subscribers</span>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="ops-summary">
        {[
          { label: 'High Risk', value: risk_distribution?.high || 0, icon: AlertTriangle, accent: 'text-rose-400 bg-rose-500/10' },
          { label: 'Medium Risk', value: risk_distribution?.medium || 0, icon: TrendingUp, accent: 'text-amber-400 bg-amber-500/10' },
          { label: 'Open Tickets', value: support?.open || 0, icon: MessageCircle, accent: 'text-blue-400 bg-blue-500/10' },
          { label: 'Urgent', value: support?.urgent || 0, icon: Zap, accent: 'text-rose-400 bg-rose-500/10' },
          { label: 'Unstable', value: mindflo?.unstable_today || 0, icon: Brain, accent: 'text-purple-400 bg-purple-500/10' },
          { label: 'No Session 3d', value: execution_gaps?.no_session_3d || 0, icon: Clock, accent: 'text-zinc-400 bg-zinc-500/10' },
        ].map(s => (
          <Card key={s.label} className="bg-adm-surface border-white/[0.06]">
            <CardContent className="py-3 px-3 flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-lg ${s.accent.split(' ')[1]} flex items-center justify-center`}>
                <s.icon className={`w-4 h-4 ${s.accent.split(' ')[0]}`} />
              </div>
              <div>
                <div className="text-lg font-['Outfit'] font-semibold text-adm-primary">{s.value}</div>
                <div className="text-[9px] text-adm-tertiary">{s.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Top Priority Subscribers */}
      <Card className="bg-adm-surface border-white/[0.06]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-['Outfit'] font-medium text-adm-primary flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-rose-400" /> Today's Priority
            </h3>
            <button onClick={() => navigate('/centre/subscribers')} className="text-[10px] text-adm-accent flex items-center gap-0.5 hover:underline" data-testid="view-all-subs">
              View all <ChevronRight className="w-3 h-3" />
            </button>
          </div>

          {(top_priority || []).length === 0 ? (
            <div className="text-center py-6 text-adm-tertiary text-xs">No subscribers in scope</div>
          ) : (
            <div className="space-y-2">
              {top_priority.map((s, i) => (
                <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] transition-colors group" data-testid={`priority-${i}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${s.priority_score >= 50 ? 'bg-rose-500/20 text-rose-400' : s.priority_score >= 25 ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-['Outfit'] font-medium text-adm-primary truncate">{s.name}</span>
                      <Badge className={`text-[8px] border-0 ${s.priority_score >= 50 ? 'bg-rose-500/15 text-rose-400' : s.priority_score >= 25 ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                        {Math.round(s.priority_score)}
                      </Badge>
                      {s.care_state && <Badge className="text-[8px] bg-white/[0.05] text-adm-tertiary border-0">{s.care_state}</Badge>}
                    </div>
                    <div className="mt-1"><PriorityBar score={s.priority_score} /></div>
                  </div>
                  <SignalDots signals={s.signals} />
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-adm-tertiary hover:text-adm-primary" onClick={() => navigate(`/admin/care-dashboard/${s.id}`)} title="View Profile" data-testid={`quick-profile-${i}`}>
                      <Eye className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-adm-tertiary hover:text-adm-primary" onClick={() => navigate('/admin/support-os')} title="Support">
                      <MessageCircle className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Risk + Support side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* SLA Breaches */}
        {(support?.sla_breached || 0) > 0 && (
          <Card className="bg-adm-surface border-rose-500/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-rose-400" />
                <span className="text-xs font-medium text-rose-400">SLA Breached</span>
              </div>
              <div className="text-2xl font-['Outfit'] font-semibold text-rose-400">{support.sla_breached}</div>
              <p className="text-[10px] text-adm-tertiary mt-1">tickets past SLA — require immediate attention</p>
            </CardContent>
          </Card>
        )}

        {/* Triggered */}
        {(mindflo?.triggered_today || 0) > 0 && (
          <Card className="bg-adm-surface border-purple-500/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-4 h-4 text-purple-400" />
                <span className="text-xs font-medium text-purple-400">Triggered Today</span>
              </div>
              <div className="text-2xl font-['Outfit'] font-semibold text-purple-400">{mindflo.triggered_today}</div>
              <p className="text-[10px] text-adm-tertiary mt-1">subscribers in triggered state — check in urgently</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SUBSCRIBERS VIEW
// ═══════════════════════════════════════════════════════════

function SubscribersView({ navigate }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/ops/subscribers`).then(r => setSubs(r.data?.subscribers || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 text-adm-accent animate-spin" /></div>;

  return (
    <div className="space-y-2" data-testid="ops-subscribers">
      {subs.length === 0 ? (
        <div className="text-center py-16 text-adm-tertiary text-xs">No subscribers in your accessible scope</div>
      ) : subs.map((s, i) => (
        <Card key={s.id} className="bg-adm-surface border-white/[0.06] hover:border-white/[0.1] transition-colors">
          <CardContent className="py-3 px-4 flex items-center gap-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${s.priority_score >= 50 ? 'bg-rose-500/15 text-rose-400' : s.priority_score >= 25 ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
              {Math.round(s.priority_score)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-['Outfit'] font-medium text-adm-primary">{s.name}</span>
                {s.care_state && <Badge className="text-[8px] bg-white/5 text-adm-tertiary border-0">{s.care_state}</Badge>}
                {s.mindflo_state && <Badge className={`text-[8px] border-0 ${s.mindflo_state === 'stable' ? 'bg-emerald-500/10 text-emerald-400' : s.mindflo_state === 'triggered' ? 'bg-rose-500/10 text-rose-400' : 'bg-amber-500/10 text-amber-400'}`}>{s.mindflo_state}</Badge>}
                {s.open_tickets > 0 && <Badge className="text-[8px] bg-blue-500/10 text-blue-400 border-0">{s.open_tickets} tickets</Badge>}
              </div>
              <PriorityBar score={s.priority_score} />
            </div>
            <SignalDots signals={s.signals} />
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-adm-tertiary hover:text-adm-primary" onClick={() => navigate(`/admin/care-dashboard/${s.id}`)} data-testid={`sub-profile-${i}`}>
                <Eye className="w-3 h-3 mr-1" /> Profile
              </Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-adm-tertiary hover:text-adm-primary" onClick={() => navigate('/admin/support-os')}>
                <MessageCircle className="w-3 h-3 mr-1" /> Support
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ACTIONS VIEW
// ═══════════════════════════════════════════════════════════

function ActionsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/ops/actions`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 text-adm-accent animate-spin" /></div>;
  if (!data) return <div className="text-center py-16 text-adm-tertiary text-xs">Unable to load actions</div>;

  return (
    <div className="space-y-5" data-testid="ops-actions">
      {/* Follow-ups */}
      <Card className="bg-adm-surface border-white/[0.06]">
        <CardContent className="p-4">
          <h3 className="text-xs font-['Outfit'] font-medium text-adm-primary flex items-center gap-1.5 mb-3">
            <Bell className="w-3.5 h-3.5 text-amber-400" /> Pending Follow-ups
            <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-0 ml-1">{data.followups?.length || 0}</Badge>
          </h3>
          {(data.followups || []).length === 0 ? (
            <p className="text-xs text-adm-tertiary">No pending follow-ups</p>
          ) : (
            <div className="space-y-1.5">
              {data.followups.map(f => (
                <div key={f.id} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02]">
                  <div>
                    <span className="text-xs text-adm-primary">{f.subscriber_name || f.subscriber_id}</span>
                    <span className="text-[10px] text-adm-tertiary ml-2">{f.followup_type} — {f.message?.substring(0, 60)}</span>
                  </div>
                  <Badge className={`text-[8px] border-0 ${f.status === 'missed' ? 'bg-rose-500/15 text-rose-400' : 'bg-amber-500/15 text-amber-400'}`}>{f.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Suggestions */}
      <Card className="bg-adm-surface border-white/[0.06]">
        <CardContent className="p-4">
          <h3 className="text-xs font-['Outfit'] font-medium text-adm-primary flex items-center gap-1.5 mb-3">
            <Zap className="w-3.5 h-3.5 text-adm-accent" /> Pending Suggestions
            <Badge className="text-[9px] bg-adm-accent/15 text-adm-accent border-0 ml-1">{data.pending_suggestions?.length || 0}</Badge>
          </h3>
          {(data.pending_suggestions || []).length === 0 ? (
            <p className="text-xs text-adm-tertiary">No pending suggestions</p>
          ) : (
            <div className="space-y-1.5">
              {data.pending_suggestions.map(s => (
                <div key={s.id} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02]">
                  <div>
                    <span className="text-xs text-adm-primary">{s.subscriber_name}</span>
                    <span className="text-[10px] text-adm-tertiary ml-2">{s.action_type?.replace(/_/g, ' ')} — {s.reason?.substring(0, 60)}</span>
                  </div>
                  <Badge className="text-[8px] bg-adm-accent/10 text-adm-accent border-0">{s.confidence}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Auto Actions */}
      <Card className="bg-adm-surface border-white/[0.06]">
        <CardContent className="p-4">
          <h3 className="text-xs font-['Outfit'] font-medium text-adm-primary flex items-center gap-1.5 mb-3">
            <Play className="w-3.5 h-3.5 text-emerald-400" /> Recent Auto-Actions
            <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-0 ml-1">{data.recent_auto_actions?.length || 0}</Badge>
          </h3>
          {(data.recent_auto_actions || []).length === 0 ? (
            <p className="text-xs text-adm-tertiary">No recent auto-actions</p>
          ) : (
            <div className="space-y-1.5">
              {data.recent_auto_actions.map(a => (
                <div key={a.id} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02]">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                    <span className="text-xs text-adm-primary">{a.subscriber_name}</span>
                    <span className="text-[10px] text-adm-tertiary">{a.action_type?.replace(/_/g, ' ')}</span>
                  </div>
                  <span className="text-[10px] text-adm-tertiary">{a.executed_at ? new Date(a.executed_at).toLocaleDateString() : ''}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
